import React, { useState, useEffect } from 'react';
import './App.css';
import * as StellarSdk from '@stellar/stellar-sdk';
import * as cbor from 'cbor-web';

// Contract addresses
const FORTUNE_CONTRACT_ID = 'CCVB5XLXEG2EVXRYI3XW5EPHGJZ5KJWCPU4Q6TKPJ3ARMDEWX46NSTSD';
const WALLET_FACTORY_CONTRACT_ID = 'CCAKXHXTZYGTBF66V6SBTG2ZNCBREY26JKEASMKBZCWMWSL5FNCN5C4I';
const WALLET_WASM_HASH = '57db902578ca87ddbe1cc082e702f951b768ccf97e72db687bcb8d57fb67f3cf';
const SPONSOR_ACCOUNT = 'GDAMZ6LLULDQ6ASYNYKULL7V6X6WAD7HWOC5FNH6JFTM76NBRQNTMJJB'; // Sponsor that pays for wallet deployment
const TESTNET_URL = 'https://soroban-testnet.stellar.org';

// API endpoint - works for both local dev and Vercel deployment
// In production (Vercel), API routes are on the same domain, so use empty string
// In development, use localhost backend
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');

function App() {
  const [fortune, setFortune] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passkey, setPasskey] = useState(null);
  const [walletAddress, setWalletAddress] = useState(null); // User's smart wallet contract address
  const [showProfile, setShowProfile] = useState(false);
  const [balance, setBalance] = useState('0.00');

  useEffect(() => {
    // Check if passkey and wallet exist in localStorage
    const stored = localStorage.getItem('passkeyCredentialId');
    const storedWallet = localStorage.getItem('walletAddress');

    if (stored) {
      const passkeyInfo = JSON.parse(stored);
      setPasskey(passkeyInfo);

      if (storedWallet) {
        setWalletAddress(storedWallet);
        // Fetch balance for the user's smart wallet
        fetchBalance(storedWallet);
      } else {
        // If passkey exists but no wallet, assign demo wallet
        const DEMO_WALLET = 'CDDSZLLWWU5CX3Z42POBQLWLJK4Z4G6M3WUMYM3FLNH7AD27QJ6K25Q2';
        setWalletAddress(DEMO_WALLET);
        localStorage.setItem('walletAddress', DEMO_WALLET);
        fetchBalance(DEMO_WALLET);
      }
    }
  }, []);

  // Deploy a new smart wallet for the user via the factory contract
  const deployWallet = async (publicKey) => {
    console.log('🏭 Deploying wallet via backend relay service...');

    // Convert public key to hex string
    const publicKeyHex = Array.from(publicKey).map(b => b.toString(16).padStart(2, '0')).join('');

    const response = await fetch(`${API_BASE_URL}/api/deploy-wallet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        publicKey: publicKeyHex
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to deploy wallet');
    }

    console.log('✅ Wallet deployed at:', data.walletAddress);
    return data.walletAddress;
  };

  // Fetch XLM balance from Horizon (for contract account)
  const fetchBalance = async (address) => {
    try {
      const horizonServer = new StellarSdk.Horizon.Server('https://horizon-testnet.stellar.org');
      const account = await horizonServer.loadAccount(address);

      // Find XLM balance
      const xlmBalance = account.balances.find(
        balance => balance.asset_type === 'native'
      );

      if (xlmBalance) {
        // Show full precision (7 decimal places for XLM)
        setBalance(parseFloat(xlmBalance.balance).toFixed(7));
      }
    } catch (err) {
      // Contract addresses don't show up in Horizon like regular accounts
      // This is expected for smart wallet contracts
      console.log('Note: Contract addresses do not have balances in Horizon');
      setBalance('N/A (Contract)');
    }
  };

  // Function to create a passkey
  const createPasskey = async () => {
    try {
      setLoading(true);
      setError('');

      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      // Create passkey
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rp: {
            name: 'Fortune Cookie Wallet',
            id: window.location.hostname,
          },
          user: {
            id: crypto.getRandomValues(new Uint8Array(16)),
            name: 'fortune-user',
            displayName: 'Fortune Cookie User',
          },
          pubKeyCredParams: [
            {
              type: 'public-key',
              alg: -7, // ES256 (secp256r1)
            },
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform', // Use platform authenticator (Face ID/Touch ID)
            userVerification: 'required',
          },
          timeout: 60000,
          attestation: 'direct',
        },
      });

      // Extract public key from credential
      const publicKey = await extractPublicKey(credential.response);

      // Store credential info
      const passkeyInfo = {
        credentialId: Array.from(new Uint8Array(credential.rawId)),
        publicKey: Array.from(publicKey),
      };

      localStorage.setItem('passkeyCredentialId', JSON.stringify(passkeyInfo));
      setPasskey(passkeyInfo);

      console.log('✅ Passkey created!', passkeyInfo);

      // Step 2: Deploy smart wallet via factory contract
      console.log('🏭 Deploying smart wallet contract...');
      const walletAddr = await deployWallet(publicKey);

      setWalletAddress(walletAddr);
      localStorage.setItem('walletAddress', walletAddr);

      console.log('✅ Smart wallet deployed at:', walletAddr);
      alert(`Passkey and wallet created successfully!\n\nYour wallet: ${walletAddr.substring(0, 8)}...${walletAddr.substring(walletAddr.length - 6)}`);

    } catch (err) {
      console.error('Error creating passkey:', err);
      setError('Failed to create passkey: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Extract public key from WebAuthn credential
  const extractPublicKey = async (response) => {
    try {
      const attestationObject = response.attestationObject;

      // Decode CBOR attestation object
      const attestation = await cbor.decodeFirst(attestationObject);
      const authData = attestation.authData;

      // Parse authData to extract public key
      // Structure: rpIdHash (32) + flags (1) + signCount (4) + attestedCredentialData
      const rpIdHash = authData.slice(0, 32);
      const flags = authData[32];
      const signCount = authData.slice(33, 37);

      // AAGUID (16 bytes)
      const aaguid = authData.slice(37, 53);

      // Credential ID length (2 bytes)
      const credIdLenBytes = authData.slice(53, 55);
      const credIdLen = new DataView(credIdLenBytes.buffer, credIdLenBytes.byteOffset).getUint16(0, false);

      // Credential ID
      const credentialId = authData.slice(55, 55 + credIdLen);

      // COSE public key starts after credential ID
      const cosePublicKey = authData.slice(55 + credIdLen);

      // Decode COSE public key
      const publicKeyObject = await cbor.decodeFirst(cosePublicKey);

      // Extract x and y coordinates (for ES256/secp256r1)
      // COSE key parameters: -2 is x coordinate, -3 is y coordinate
      const x = publicKeyObject.get(-2);
      const y = publicKeyObject.get(-3);

      // Create uncompressed public key (0x04 + x + y)
      const publicKey = new Uint8Array(65);
      publicKey[0] = 0x04; // Uncompressed point indicator
      publicKey.set(new Uint8Array(x), 1);
      publicKey.set(new Uint8Array(y), 33);

      console.log('Extracted public key:', Array.from(publicKey).map(b => b.toString(16).padStart(2, '0')).join(''));

      return publicKey;
    } catch (err) {
      console.error('Error extracting public key:', err);
      // Return a dummy public key for demo purposes
      const publicKey = new Uint8Array(65);
      publicKey[0] = 0x04;
      crypto.getRandomValues(publicKey.subarray(1));
      return publicKey;
    }
  };

  // Parse DER-encoded ECDSA signature to raw r + s (64 bytes)
  const parseDERSignature = (derSig) => {
    // DER format: 0x30 [total-length] 0x02 [r-length] [r] 0x02 [s-length] [s]
    let offset = 0;

    // Check DER sequence tag
    if (derSig[offset++] !== 0x30) {
      throw new Error('Invalid DER signature: missing sequence tag');
    }

    // Skip total length
    offset++;

    // Read r
    if (derSig[offset++] !== 0x02) {
      throw new Error('Invalid DER signature: missing r integer tag');
    }
    const rLength = derSig[offset++];
    let r = derSig.slice(offset, offset + rLength);
    offset += rLength;

    // Read s
    if (derSig[offset++] !== 0x02) {
      throw new Error('Invalid DER signature: missing s integer tag');
    }
    const sLength = derSig[offset++];
    let s = derSig.slice(offset, offset + sLength);

    // Remove leading zeros if present (DER encoding can add them)
    while (r.length > 32 && r[0] === 0) {
      r = r.slice(1);
    }
    while (s.length > 32 && s[0] === 0) {
      s = s.slice(1);
    }

    // Pad to 32 bytes if needed
    const rPadded = new Uint8Array(32);
    const sPadded = new Uint8Array(32);
    rPadded.set(r, 32 - r.length);
    sPadded.set(s, 32 - s.length);

    // Normalize s to low form (if s > n/2, then s = n - s)
    // secp256r1 curve order n
    const n = new Uint8Array([
      0xFF, 0xFF, 0xFF, 0xFF, 0x00, 0x00, 0x00, 0x00,
      0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF,
      0xBC, 0xE6, 0xFA, 0xAD, 0xA7, 0x17, 0x9E, 0x84,
      0xF3, 0xB9, 0xCA, 0xC2, 0xFC, 0x63, 0x25, 0x51
    ]);

    // n/2
    const halfN = new Uint8Array([
      0x7F, 0xFF, 0xFF, 0xFF, 0x80, 0x00, 0x00, 0x00,
      0x7F, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF,
      0xDE, 0x73, 0x7D, 0x56, 0xD3, 0x8B, 0xCF, 0x42,
      0x79, 0xDC, 0xE5, 0x61, 0x7E, 0x31, 0x92, 0xA8
    ]);

    // Compare s with halfN
    let sIsHigh = false;
    for (let i = 0; i < 32; i++) {
      if (sPadded[i] > halfN[i]) {
        sIsHigh = true;
        break;
      } else if (sPadded[i] < halfN[i]) {
        break;
      }
    }

    // If s is high, compute s = n - s
    if (sIsHigh) {
      console.log('Normalizing high s value...');
      let borrow = 0;
      for (let i = 31; i >= 0; i--) {
        let diff = n[i] - sPadded[i] - borrow;
        if (diff < 0) {
          diff += 256;
          borrow = 1;
        } else {
          borrow = 0;
        }
        sPadded[i] = diff;
      }
    }

    // Combine r + s into 64-byte signature
    const signature = new Uint8Array(64);
    signature.set(rPadded, 0);
    signature.set(sPadded, 32);

    console.log('Parsed signature (64 bytes):', Array.from(signature).map(b => b.toString(16).padStart(2, '0')).join(''));

    return signature;
  };

  // Sign a message with the passkey
  const signWithPasskey = async (message) => {
    try {
      if (!passkey) {
        throw new Error('No passkey found. Please create one first.');
      }

      const credentialId = new Uint8Array(passkey.credentialId);

      // Use the message as the challenge
      const challenge = new TextEncoder().encode(message);

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          allowCredentials: [{
            type: 'public-key',
            id: credentialId,
          }],
          userVerification: 'required',
          timeout: 60000,
        },
      });

      // Extract WebAuthn response components
      const authenticatorData = new Uint8Array(assertion.response.authenticatorData);
      const clientDataJSON = new Uint8Array(assertion.response.clientDataJSON);
      const derSignature = new Uint8Array(assertion.response.signature);

      // Parse DER signature to extract r and s
      const signature = parseDERSignature(derSignature);

      return {
        authenticatorData,
        clientDataJSON,
        signature
      };
    } catch (err) {
      throw new Error('Failed to sign with passkey: ' + err.message);
    }
  };

  // Get a fortune using passkey wallet contract
  const getFortune = async () => {
    try {
      setLoading(true);
      setError('');

      if (!passkey) {
        throw new Error('Please create a passkey first');
      }

      if (!walletAddress) {
        throw new Error('No wallet deployed. Please create a passkey first to deploy your wallet.');
      }

      // Step 1: Sign a message with the passkey
      const message = 'Get fortune from cookie';
      console.log('🔐 Requesting passkey signature...');
      const { authenticatorData, clientDataJSON, signature } = await signWithPasskey(message);
      console.log('✅ Passkey signature obtained');

      // Step 2: Send to backend relay service
      console.log('📤 Sending to backend relay service...');

      const authenticatorDataHex = Array.from(authenticatorData).map(b => b.toString(16).padStart(2, '0')).join('');
      const clientDataJSONHex = Array.from(clientDataJSON).map(b => b.toString(16).padStart(2, '0')).join('');
      const signatureHex = Array.from(signature).map(b => b.toString(16).padStart(2, '0')).join('');

      const response = await fetch(`${API_BASE_URL}/api/get-fortune`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authenticatorData: authenticatorDataHex,
          clientDataJSON: clientDataJSONHex,
          signature: signatureHex,
          walletAddress: walletAddress
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get fortune');
      }

      console.log('🎉 Fortune received:', data.fortune);
      setFortune(data.fortune);

    } catch (err) {
      console.error('Error getting fortune:', err);
      setError('Failed to get fortune: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper to truncate addresses
  const truncateAddress = (addr) => {
    if (!addr) return '';
    return `${addr.substring(0, 8)}...${addr.substring(addr.length - 6)}`;
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🥠 Fortune Cookie Dispenser</h1>
        <p className="subtitle">Powered by Soroban - Stellar Smart Contracts</p>

        {passkey && (
          <div className="profile-card">
            <button
              className="profile-toggle"
              onClick={() => setShowProfile(!showProfile)}
            >
              {showProfile ? '▼' : '▶'} My Wallet
            </button>

            {showProfile && (
              <div className="profile-details">
                <div className="profile-item">
                  <label>Balance:</label>
                  <span className="balance">{balance} XLM</span>
                </div>
                <div className="profile-item">
                  <label>Passkey Wallet:</label>
                  <span className="address" title={walletAddress}>
                    {truncateAddress(walletAddress)}
                  </span>
                </div>
                <div className="profile-item">
                  <label>Fortune Contract:</label>
                  <span className="address" title={FORTUNE_CONTRACT_ID}>
                    {truncateAddress(FORTUNE_CONTRACT_ID)}
                  </span>
                </div>
                <div className="profile-item">
                  <label>Network:</label>
                  <span>Stellar Testnet</span>
                </div>
                <div className="profile-item small">
                  <label>Price per Fortune:</label>
                  <span>0.1 XLM</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="card">
          {!passkey ? (
            <div>
              <h2>Create Your Passkey Wallet</h2>
              <p>Use Face ID or Touch ID to secure your authentication</p>
              <button
                onClick={createPasskey}
                disabled={loading}
                className="primary-button"
              >
                {loading ? 'Creating...' : '🔐 Create Passkey'}
              </button>
            </div>
          ) : (
            <div>
              <h2>🎉 Ready to Go!</h2>
              <p className="success">✅ Passkey authenticated | ✅ Wallet deployed ({truncateAddress(walletAddress)})</p>

              <button
                onClick={getFortune}
                disabled={loading}
                className="fortune-button"
              >
                {loading ? 'Getting Fortune...' : '🥠 Get Fortune'}
              </button>

              {fortune && (
                <div className="fortune-display">
                  <h3>Your Fortune:</h3>
                  <p className="fortune-text">{fortune}</p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="error">
              ⚠️ {error}
            </div>
          )}
        </div>

        <div className="info-card">
          <h3>How It Works</h3>
          <ol>
            <li>Create a passkey using Face ID/Touch ID for authentication</li>
            <li>Factory contract deploys your personal smart wallet</li>
            <li>Your wallet is initialized with your passkey public key</li>
            <li>Click "Get Fortune" to test the flow</li>
            <li>Authenticate with your passkey to sign the transaction</li>
            <li>Smart wallet verifies your signature on-chain</li>
            <li>Wallet calls the Fortune contract on your behalf</li>
          </ol>
        </div>
      </header>
    </div>
  );
}

export default App;
